/**
 * LeistungenKarussell
 * - 3 fixe Hauptkarten nebeneinander: Für Eigentümer, Für Käufer, Für Tippgeber
 * - Manuelles 3er-Karussell für Einzelleistungen (immer 3 gleichzeitig sichtbar)
 * - Keine Preise
 */
import { useState } from "react";
import { Link } from "wouter";
import { ChevronLeft, ChevronRight, CheckCircle, ArrowRight } from "lucide-react";

const HAUPTKARTEN = [
  {
    icon: "🏠",
    title: "Für Eigentümer",
    desc: "Ob Full-Service oder gezielte Unterstützung beim Selbstverkauf – wir finden gemeinsam die richtige Strategie für Ihr Objekt",
    punkte: [
      "Marktgerechte Wertermittlung",
      "Professionelles Exposé & Fotos",
      "Vermarktung & Besichtigungen",
      "Begleitung bis zum Notartermin",
    ],
    cta: "Jetzt bewerten lassen",
    href: "/kontakt",
    farbe: "#631172",
  },
  {
    icon: "🔑",
    title: "Für Käufer",
    desc: "Sie haben eine Immobilie im Blick? Ich prüfe Unterlagen, schätze Risiken ein und begleite Sie – damit Sie keine bösen Überraschungen erleben",
    punkte: [
      "Prüfung von Unterlagen & Kaufvertrag",
      "Einschätzung von Risiken & Chancen",
      "Begleitung zu Besichtigungen",
      "Koordination mit Finanzierung & Notar",
    ],
    cta: "Kaufbegleitung anfragen",
    href: "/kontakt",
    farbe: "#a066c2",
  },
  {
    icon: "🤝",
    title: "Für Tippgeber",
    desc: "Sie kennen jemanden, der über einen Verkauf nachdenkt? Oder haben Sie einen passenden Käufer? Sprechen Sie mich an",
    punkte: [
      "Kein bürokratischer Aufwand für Sie",
      "Diskrete Weitergabe des Kontakts",
      "Schnelle Rückmeldung",
      "Faire Tippgeberprovision bei Abschluss",
    ],
    cta: "Tipp weitergeben",
    href: "/kontakt",
    farbe: "#631172",
  },
];

const EINZELLEISTUNGEN = [
  {
    icon: "📊",
    title: "Wertermittlung",
    desc: "Fundierte Marktpreisanalyse auf Basis aktueller Vergleichsdaten – kostenlos und unverbindlich",
    farbe: "#631172",
  },
  {
    icon: "📸",
    title: "Fototermin & Exposé",
    desc: "Professionelle Immobilienfotografie und verkaufsstarker Exposé-Text für maximale Wirkung",
    farbe: "#a066c2",
  },
  {
    icon: "📁",
    title: "Dokumentenbeschaffung",
    desc: "Grundbuch, Energieausweis, Baupläne, WEG-Unterlagen – ich kümmere mich um alles",
    farbe: "#631172",
  },
  {
    icon: "⚖️",
    title: "Notar & Kaufvertrag",
    desc: "Notarbeauftragung, Prüfung und Optimierung des Kaufvertragsentwurfs – sicher bis zum Abschluss",
    farbe: "#a066c2",
  },
  {
    icon: "📐",
    title: "Grundrisse aufbereiten",
    desc: "Vorhandene Pläne verkaufsfördernd aufbereiten und modern darstellen. Keine Neuerstellung",
    farbe: "#631172",
  },
  {
    icon: "✅",
    title: "Checklisten & Leitfäden",
    desc: "Praxiserprobte Checklisten für Verkäufer und Käufer – damit nichts vergessen wird",
    farbe: "#a066c2",
  },
  {
    icon: "💼",
    title: "Beratung Selbstverkauf",
    desc: "Sie möchten selbst verkaufen, brauchen aber Unterstützung? Ich stehe Ihnen zur Seite",
    farbe: "#631172",
  },
  {
    icon: "🏠",
    title: "Full-Service Vermarktung",
    desc: "Alles aus einer Hand – in meiner Provision ist alles inkludiert, keine versteckten Kosten",
    farbe: "#a066c2",
  },
];

interface Props {
  scrollTo: (id: string) => void;
}

export default function LeistungenKarussell({ scrollTo }: Props) {
  const [page, setPage] = useState(0);
  const perPage = 3;
  const totalPages = Math.ceil(EINZELLEISTUNGEN.length / perPage);

  const visible = EINZELLEISTUNGEN.slice(page * perPage, page * perPage + perPage);

  return (
    <section
      id="leistungen"
      className="py-20"
      style={{ background: "linear-gradient(135deg, #faf8f5 0%, #f0e8f8 100%)" }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-sm font-bold tracking-[0.2em] uppercase mb-3" style={{ color: "#8a6a20" }}>
            Was ich für Sie tue
          </p>
          <h2 className="text-3xl lg:text-4xl font-bold text-[#2d1b4e]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Meine <span className="italic">Leistungen</span>
          </h2>
          <p className="text-gray-500 mt-3 max-w-xl mx-auto text-sm">
            Von der Wertermittlung bis zum Notartermin – alles aus einer Hand, ohne versteckte Kosten
          </p>
        </div>

        {/* 3 fixe Hauptkarten – komplett klickbar zum Kontaktformular */}
        <div className="grid md:grid-cols-3 gap-6 mb-14">
          {HAUPTKARTEN.map((card, i) => (
            <Link key={i} href={card.href}>
              <div
                className="bg-white rounded-2xl p-7 border border-gray-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col h-full cursor-pointer group"
                style={{ borderTop: `3px solid ${card.farbe}` }}
              >
                <div className="text-4xl mb-4">{card.icon}</div>
                <h3 className="text-xl font-bold text-[#2d1b4e] mb-3 group-hover:text-[#631172] transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {card.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-5">{card.desc}</p>
                <ul className="space-y-2 mb-6 flex-1">
                  {card.punkte.map((punkt, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckCircle size={14} className="mt-0.5 flex-shrink-0" style={{ color: card.farbe }} />
                      {punkt}
                    </li>
                  ))}
                </ul>
                <div
                  className="mt-auto inline-flex items-center gap-2 text-sm font-bold transition-all group-hover:gap-3"
                  style={{ color: card.cta === "Kaufbegleitung anfragen" ? "#6b3f86" : card.farbe }}
                >
                  {card.cta} <ArrowRight size={14} />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Trennlinie */}
        <div className="flex items-center gap-4 mb-10">
          <div className="h-px flex-1 bg-[#e8dff0]" />
          <p className="text-sm font-bold text-[#631172] tracking-[0.15em] uppercase whitespace-nowrap">
            Einzelleistungen im Detail
          </p>
          <div className="h-px flex-1 bg-[#e8dff0]" />
        </div>

        {/* Manuelles 3er-Karussell – komplett klickbar zum Kontaktformular */}
        <div className="relative">
          <div className="grid md:grid-cols-3 gap-6">
            {visible.map((l, i) => (
              <Link key={page * perPage + i} href="/kontakt">
                <div
                  className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-md transition-all duration-300 hover:-translate-y-1 h-full cursor-pointer group"
                  style={{ borderLeft: `3px solid ${l.farbe}` }}
                >
                  <div className="text-3xl mb-3">{l.icon}</div>
                  <h4 className="font-bold text-[#2d1b4e] mb-2 group-hover:text-[#631172] transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {l.title}
                  </h4>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">{l.desc}</p>
                  <div className="inline-flex items-center gap-1.5 text-xs font-bold text-[#631172] group-hover:gap-2 transition-all">
                    Termin anfragen <ArrowRight size={12} />
                  </div>
                </div>
              </Link>
            ))}
            {/* Platzhalter wenn letzte Seite weniger als 3 hat */}
            {visible.length < perPage &&
              Array.from({ length: perPage - visible.length }).map((_, i) => (
                <div key={`ph-${i}`} className="hidden md:block" />
              ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              aria-label="Vorherige Leistung"
              className="w-11 h-11 rounded-full bg-white shadow border border-[#e8dff0] flex items-center justify-center transition-all disabled:opacity-30 hover:bg-[#f0e8f8]"
            >
              <ChevronLeft size={18} className="text-[#631172]" />
            </button>

            <div className="flex gap-2">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i)}
                  className="w-11 h-11 inline-flex items-center justify-center rounded-full transition-all duration-300"
                  aria-label={`Leistungsseite ${i + 1} von ${totalPages}`}
                >
                  <span
                    aria-hidden="true"
                    className="block h-2 rounded-full transition-all duration-300"
                    style={{
                      width: i === page ? "24px" : "8px",
                      background: i === page ? "#631172" : "#d4b8e8",
                    }}
                  />
                </button>
              ))}
            </div>

            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page === totalPages - 1}
              aria-label="Nächste Leistung"
              className="w-11 h-11 rounded-full bg-white shadow border border-[#e8dff0] flex items-center justify-center transition-all disabled:opacity-30 hover:bg-[#f0e8f8]"
            >
              <ChevronRight size={18} className="text-[#631172]" />
            </button>
          </div>

          <div className="text-center mt-3 text-xs text-gray-400">
            {page + 1} / {totalPages}
          </div>
        </div>

        {/* Link zur vollständigen Leistungsseite */}
        <div className="text-center mt-10">
          <a
            href="/leistungen"
            className="inline-flex min-h-11 items-center justify-center gap-2 py-2 text-sm font-bold transition-all hover:gap-3"
            style={{ color: "#631172" }}
          >
            Alle Leistungen im Detail ansehen <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </section>
  );
}
